import { faAngleDown, faAngleUp, faCheck, faSpinner } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useJsApiLoader } from "@react-google-maps/api"
import ObjectID from "bson-objectid"
import { exhaustive } from "exhaustive"
import { isEqual, isNumber, orderBy, some, sortBy, sum } from "lodash"
import { DateTime } from "luxon"
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useParams } from "react-router-dom"
import { v4 } from "uuid"
import { z } from "zod"
import { AbsolutCentered } from "../../AbsolutCentered/AbsolutCentered"
import { useAppIsFocused } from "../../AppIsFocusedContext"
import { useAuth } from "../../Auth/AuthContext"
import { BranchTargetKeyEnum } from "../../Client/articleTrees/ArticleTreeDataModel"
import { useClient } from "../../Client/ClientAndUserProvider"
import { PreferredVATRenderPolicy } from "../../Client/consumerCatalogConfigurationTypes"
import { OrderActivityRenderMode, OrderDetailsRenderMode, OrderReferenceMode } from "../../Client/FeatureTypes"
import { CoredinationMode } from "../../Client/GetClientResponse"
import { UpdateOrderStateAndComment } from "../../ClientPortal/OrderAcceptance/OrderAcceptance"
import {
	BlankCalendar,
	BriefcaseDollar,
	ChatBubbleSquareWrite,
	ChatBubbleTextSquare,
	CircleClock,
	CloudShare,
	CrossIcon,
	EmptyClipboard,
	IncomingCall,
	LeftArrowIcon,
	LocationPin3,
	MailSendEnvelope,
	MapPin,
	OutgoingCall,
	Paperclip1,
	PenIcon,
	ReceiptSubtract,
	ShippingTruck,
	ShoppingBasket2,
	TagIcon,
	UserCheckValidate,
	UserMultipleGroup,
	UserSingleNeutralMale,
	WaveSignal,
} from "../../Icons/Icon"
import { Loader } from "../../Loader/Loader"
import { useNavigator } from "../../Navigator/useNavigator"
import { API, ServerError } from "../../network/API"
import { OrderState } from "../../OrderAcceptance/OrderCollection"
import { AccentButton, AcceptButton, DenyButton, FinalizeButton } from "../../Orders/Components/Form/Buttons/Buttons"
import {
	formatPhoneNumberForSweden,
	formatPhoneNumberForSwedenToInternational,
} from "../../Orders/Components/Form/constants"
import { MultiStateSwitch } from "../../Orders/Components/Form/MultiStateSwitch/MultiStateSwitch"
import { ToggleSwitchSlimWithText } from "../../Orders/Components/Form/ToggleSwitchSlimWithText/ToggleSwitchSlimWithText"
import { HorizontalScrollBox } from "../../Orders/Components/HorizontalScrollBox/HorizontalScrollBox"
import { ModulePopup } from "../../Orders/Components/ModulePopup/ModulePopup"
import { ProductImage } from "../../Orders/Components/ProductImage/ProductImage"
import { TransportDescription } from "../../Orders/Components/TransportDescription/TransportDescription"
import { convertTextToInitials, parseNumberSafe } from "../../Orders/Helpers"
import { completedOrderItemDateElementContent } from "../../Orders/OrderCompleted/OrderCompletedSummary/OrderCompletedSummary"
import { productServiceUnitToHumanText } from "../../Orders/OrderContainer/Logic"
import {
	googleMiniMapOptions,
	libraries,
	stockholmCenterCoordinates,
} from "../../Orders/ProjectInputModule/ProjectInputModule"
import { unitFormatter } from "../../Orders/unit-formatter"
import { PermissionAreaLocation, usePermissions } from "../../PermissionContext"
import { ProductImageType, Transportation } from "../../ProductDefinitions"
import { cls } from "../../Shared/cls"
import { ConfirmModal, ConfirmModalCloseReason, ConfirmModalRefProps } from "../../Shared/ConfirmModal/ConfirmModal"
import { getKey } from "../../Shared/getKey"
import {
	addAddressAndExactDeliveryMarkersToMap,
	addTransportZoneOverlaysToMap,
	CustomPolygon,
	GoogleMapComponent,
	initGoogleMapsCustomFullscreenControl,
	maybeSetMapBoundsForExactDelivery,
	openCoordinatesInGoogleMaps,
} from "../../Shared/GoogleMapComponent/GoogleMapComponent"
import { notNull } from "../../Shared/notNull"
import { currencyFormatter } from "../../Shared/numberFormatter"
import { InlineViewTab, TabbedInlineView } from "../../Shared/TabbedInlineView/TabbedInlineView"
import { useBrandedLocalStorage } from "../../Shared/useBrandedLocalStorage"
import { when } from "../../Shared/when"
import { GetConsumerTag, GetConsumerTags } from "../CustomerPortalConsumerTags/CustomerPortalConsumerTags"
import {
	ArticleType,
	Contact,
	GetOrder,
	GetOrderFile,
	GetOrderLoggedInUser,
	getSpecificOrderForClient,
	getSpecificOrderForConsumer,
	OrderLoggedInUserType,
	OrderProduct,
	OrderStateToHumanText,
} from "../CustomerPortalOrders/CustomerPortalOrders"
import { GetUploadedFiles, OrderActivity, OrderDetailsActivity } from "../OrderDetails/ActivityTab/OrderDetailsActivity"
import {
	Files,
	OrderDetailsAttachments,
	orderFileToSelectedImage,
} from "../OrderDetails/AttachmentsTab/OrderDetailsAttachments"
import {
	CoredinationJobReference,
	CoredinationOrderReference,
	ExternalReference,
	ExternalReferenceSource,
	SetOrderReferenceResponse,
	UpdateCustomOrderReference,
} from "../OrderDetails/OrderDetails"
import style from "./FullPageOrderDetails.module.css"

type FullPageOrderDetailsProps = {}

enum ShowActivitiesType {
	External = "External",
	Internal = "Internal",
}

type UpdateOrderStateDirectly = {
	newState: OrderState
}

enum MobileOnlyTabs {
	Order = "Order",
	Activities = "Activities",
	Attachments = "Attachments",
}

export const FullPageOrderDetails: FC<FullPageOrderDetailsProps> = () => {
	const { orderId } = useParams()
	const client = useClient()
	const navigator = useNavigator()
	const permissions = usePermissions()
	const auth = useAuth()
	const isAppFocused = useAppIsFocused()
	const location = useLocation()

	const viewportWidth = useRef(
		parseNumberSafe(
			window.getComputedStyle(document.documentElement).getPropertyValue("--100vw"),
			document.body.getBoundingClientRect().width,
		),
	)

	const mapRef = useRef<google.maps.Map | null>(null)
	const currentMapMarkers = useRef<google.maps.marker.AdvancedMarkerElement[]>([])

	const [order, setOrder] = useState<GetOrder | null>(null)

	const [showActivitiesType, setShowActivitiesType] = useState<ShowActivitiesType>(ShowActivitiesType.External)

	const [showUpdateOrderStateDropdown, setShowUpdateOrderStateDropdown] = useState<{
		positionX: number
		positionY: number
	} | null>(null)
	const [updatingOrderState, setUpdatingOrderState] = useState(false)

	const [updating, setUpdating] = useState(false)
	const [editCustomOrderReference, setEditCustomOrderReference] = useState(false)
	const customOrderReferenceInputRef = useRef<HTMLInputElement | null>(null)

	const [attachments, setAttachments] = useState<GetOrderFile[]>([])
	const [activities, setActivities] = useState<OrderActivity[] | null>(null)

	const [preferredVATRenderPolicy, setPreferredVATRenderPolicy] = useState<PreferredVATRenderPolicy>(
		PreferredVATRenderPolicy.ExcludeVAT,
	)

	const [selectedOutcome, setSelectedOutcome] = useState<"approve" | "deny" | null>(null)
	const [acceptOrDenyComment, setAcceptOrDenyComment] = useState<string>("")

	const [showAllContactPersons, setShowAllContactPersons] = useState(false)

	const [under711Width, setUnder711Width] = useState(viewportWidth.current < 711)
	const [under1101Width, setUnder1101Width] = useState(viewportWidth.current < 1101)
	const [under1900Width, setUnder1900Width] = useState(viewportWidth.current < 1900)

	const [showFloatingActivitiesButton, setShowFloatingActivitiesButton] = useState<boolean>(
		under1900Width && !under1101Width,
	)
	const [showActivitiesInModal, setShowActivitiesInModal] = useState(false)

	const [showMapSection, setShowMapSection] = useState(false)

	const confirmModalRef = useRef<ConfirmModalRefProps | null>(null)

	const mainMapOverlaysRef = useRef<CustomPolygon[]>([])
	const mainMapOverlaysMarkersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([])

	const [orderConsumerTags, setOrderConsumerTags] = useState<GetConsumerTag[]>([])

	const [showConsumerTags, setShowConsumerTags] = useState(false)

	const [selectedMobileOnlyTab, setSelectedMobileOnlyTab] = useState<MobileOnlyTabs>(MobileOnlyTabs.Order)

	const [showMobileOnlyTabs, setShowMobileOnlyTabs] = useState(true)

	const mobileOnlyTabsScrollTopPrevious = useRef(0)
	const scrollDownAmount = useRef(0)
	const scrollUpAmount = useRef(0)
	const scrollDownAmountThreshold = 80
	const scrollUpAmountThreshold = 60

	const { isLoaded: isGoogleMapsLoaded } = useJsApiLoader({
		id: "google-map-script",
		googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string,
		libraries: libraries,
		language: "sv",
	})

	const [selectedConsumerId] = useBrandedLocalStorage("selected-consumer-id", z.string(), {
		defaultValue: "",
	})

	const isAllowedToView = useMemo(() => {
		return permissions.isAllowed(PermissionAreaLocation.Orders_Read) || !orderId
	}, [orderId, permissions])

	const onMainPageScroll = useCallback(
		(event: Event) => {
			const element = event.target as HTMLDivElement | null

			if (!element) {
				return
			}

			if (under1101Width) {
				if (element.scrollTop > mobileOnlyTabsScrollTopPrevious.current) {
					// scrolling down
					scrollUpAmount.current = 0
					scrollDownAmount.current += element.scrollTop - mobileOnlyTabsScrollTopPrevious.current
					if (scrollDownAmount.current > scrollDownAmountThreshold) {
						setShowMobileOnlyTabs(false)
					}
				} else if (element.scrollTop < mobileOnlyTabsScrollTopPrevious.current) {
					// scrolling up
					scrollDownAmount.current = 0
					scrollUpAmount.current += mobileOnlyTabsScrollTopPrevious.current - element.scrollTop
					if (scrollUpAmount.current > scrollUpAmountThreshold) {
						setShowMobileOnlyTabs(true)
					}
				}
			}
			mobileOnlyTabsScrollTopPrevious.current = element.scrollTop
		},
		[under1101Width],
	)

	useEffect(() => {
		const mainPageLayout = document.getElementById("mainLayoutPageContent")

		if (mainPageLayout) {
			mainPageLayout.addEventListener("scroll", onMainPageScroll)

			return () => {
				mainPageLayout.removeEventListener("scroll", onMainPageScroll)
			}
		}

		return () => {}
	}, [onMainPageScroll])

	useEffect(() => {
		setShowMobileOnlyTabs(true)
	}, [location.pathname])

	useEffect(() => {
		if (!isAllowedToView) {
			navigator.open("order", undefined, true)
		} else if (client.features.orderDetailsRenderMode === OrderDetailsRenderMode.Modal) {
			navigator.open(`my-pages/orders?order=${orderId}&tabId=Booking`, undefined, true)
		}
	}, [client.features.orderDetailsRenderMode, isAllowedToView, navigator, orderId])

	const fetchOrder = useCallback(() => {
		if (!orderId || (auth.IsLoggedInConsumer && !selectedConsumerId)) {
			return Promise.resolve()
		}

		const promise = auth.IsLoggedInConsumer
			? getSpecificOrderForConsumer(client.identifier, selectedConsumerId, orderId)
			: getSpecificOrderForClient(client.identifier, orderId)

		return promise
			.then((order) => {
				setOrder(order)
				fetchConsumerTags(order.consumerId || "")
				setAcceptOrDenyComment(order.clientComment || "")
				if (order?.consumerGroupSettings) {
					setPreferredVATRenderPolicy(
						exhaustive(order.consumerGroupSettings, "type", {
							Contract: () => {
								return PreferredVATRenderPolicy.IncludeVAT
							},
							Prepay: (it) => {
								return it.preferredVATRenderPolicy
							},
							Postpay: (it) => {
								return it.preferredVATRenderPolicy
							},
						}),
					)
				} else {
					setPreferredVATRenderPolicy(PreferredVATRenderPolicy.ExcludeVAT)
				}
			})
			.catch((e: ServerError<GetOrder>) => {
				if (e?.status === 401 || e?.status === 403) {
					navigator.open("order", undefined, true)
				} else if (e?.status === 404) {
					navigator.open("my-pages/orders", undefined, true)
				}
			})
	}, [auth.IsLoggedInConsumer, client.identifier, orderId, selectedConsumerId])

	useEffect(() => {
		fetchOrder()
	}, [fetchOrder])

	const fetchAttachments = useCallback(() => {
		if (!isAppFocused || !order || !permissions.isAllowed(PermissionAreaLocation.Order_Attachments_Read)) {
			return Promise.resolve()
		}

		return API.getWithRetries<GetUploadedFiles>(`/order-ui/order-files-v1/${client.identifier}/${order.id}`, true).then(
			(res) => {
				res.files.reduce<Files>((result, element) => {
					result[element.attachmentId] = orderFileToSelectedImage(element)
					return result
				}, {})
				if (!isEqual(attachments, res.files)) {
					setAttachments(res.files)
				}
			},
			() => {},
		)
	}, [attachments, client.identifier, isAppFocused, order, permissions])

	const fetchActivities = useCallback(async () => {
		if (
			!isAppFocused ||
			!order ||
			client.features.orderActivityRenderMode === OrderActivityRenderMode.NoRender ||
			!permissions.isAllowed(PermissionAreaLocation.Order_Activities_Read)
		) {
			if (permissions.isAllowed(PermissionAreaLocation.Order_Attachments_Read)) {
				await fetchAttachments()
			}

			return Promise.resolve()
		}

		await fetchAttachments()
		return API.getWithRetries<OrderActivity[]>(
			`/customer-portal/order-activities-v1/${client.identifier}/${order.id}`,
			true,
		).then(
			(res) => {
				const newValues = sortBy(res, "createdAt").reverse()
				if (!isEqual(activities, newValues)) {
					setActivities(newValues)
				}
			},
			() => {},
		)
	}, [
		activities,
		client.features.orderActivityRenderMode,
		client.identifier,
		fetchAttachments,
		isAppFocused,
		order,
		permissions,
	])

	useEffect(() => {
		fetchActivities()

		const id = setInterval(() => {
			fetchActivities()
		}, 8000)

		return () => {
			clearInterval(id)
		}
	}, [fetchActivities])

	useEffect(() => {
		setShowFloatingActivitiesButton(under1900Width && !under711Width)

		if (!under1900Width || under1101Width) {
			setShowActivitiesInModal(false)
		}

		if (!under711Width) {
			setSelectedMobileOnlyTab(MobileOnlyTabs.Order)
		}
	}, [under1101Width, under1900Width, under711Width])

	useEffect(() => {
		const value = selectedMobileOnlyTab
		const footerElement = document.getElementsByTagName("footer").item(0)

		if (!footerElement) {
			return
		}

		if (value === MobileOnlyTabs.Activities) {
			footerElement.style.display = "none"
		} else {
			footerElement.style.display = ""
		}

		return () => {
			if (value === MobileOnlyTabs.Activities) {
				footerElement.style.display = "none"
			} else {
				footerElement.style.display = ""
			}
		}
	}, [selectedMobileOnlyTab])

	const onResize = useCallback(() => {
		viewportWidth.current = parseNumberSafe(
			window.getComputedStyle(document.documentElement).getPropertyValue("--100vw"),
			document.body.getBoundingClientRect().width,
		)

		setUnder711Width(viewportWidth.current < 711)
		setUnder1101Width(viewportWidth.current < 1100)
		setUnder1900Width(viewportWidth.current < 1900)
	}, [])

	useEffect(() => {
		window.addEventListener("resize", onResize)

		return () => {
			window.removeEventListener("resize", onResize)
		}
	}, [onResize])

	function fetchConsumerTags(consumerId: string) {
		if (!auth.IsLoggedInClient || !consumerId) {
			return
		}

		setOrderConsumerTags([])

		API.get<GetConsumerTags>(`/customer-portal/consumer-tags-v1/${client.identifier}`, true).then(
			(res) => {
				const filtered = res.tags.filter((x) => x.consumerRefs.includes(consumerId))
				setOrderConsumerTags(filtered)
			},
			() => {
				setOrderConsumerTags([])
			},
		)
	}

	function getCurrentOrderDetails(): GetOrder | null {
		return order
	}

	function canEditState(state: OrderState) {
		let canEdit

		if (state === OrderState.Declined) {
			canEdit = auth.IsLoggedInClient && permissions.isAllowed(PermissionAreaLocation.Client_Orders_Reopen_Declined)
		} else if (state === OrderState.Done) {
			canEdit = auth.IsLoggedInClient && permissions.isAllowed(PermissionAreaLocation.Client_Orders_Reopen_Done)
		} else {
			canEdit = auth.IsLoggedInClient
		}

		return canEdit
	}

	function statusElement(state: OrderState): JSX.Element | null {
		const canEdit = canEditState(state)

		const onClickFunc = (event: React.MouseEvent<HTMLDivElement>) => {
			if (canEdit) {
				if (showUpdateOrderStateDropdown) {
					setShowUpdateOrderStateDropdown(null)
				} else {
					const element = document.getElementById("orderStatusElementWrapper")?.children?.item(0)

					if (element instanceof HTMLDivElement) {
						const boundingBox = element.getBoundingClientRect()
						const positionX: number = element.getBoundingClientRect().x
						const positionY: number = element.getBoundingClientRect().y + boundingBox.height + 2

						setShowUpdateOrderStateDropdown({ positionX, positionY })
					}
				}
			}
		}

		return exhaustive(state, {
			Created: () => {
				return <div className={cls(style.orderState, style.orderStateCreated)}>{OrderStateToHumanText[state]}</div>
			},
			Accepted: () => {
				return (
					<div
						className={cls(style.orderState, style.orderStateAccepted)}
						style={{ cursor: canEdit ? "pointer" : undefined }}
						onClick={(event) => {
							onClickFunc(event)
						}}>
						{OrderStateToHumanText[state]} {canEdit ? <PenIcon size={32} className={style.editIcon} /> : null}
						{statusElementDropdown(state)}
					</div>
				)
			},
			InProgress: () => {
				return (
					<div
						className={cls(style.orderState, style.orderStateAccepted)}
						style={{ cursor: canEdit ? "pointer" : undefined }}
						onClick={(event) => {
							onClickFunc(event)
						}}>
						{OrderStateToHumanText[state]} {canEdit ? <PenIcon size={32} className={style.editIcon} /> : null}
						{statusElementDropdown(state)}
					</div>
				)
			},
			Declined: () => {
				return (
					<div
						className={cls(style.orderState, style.orderStateDeclined)}
						style={{ cursor: canEdit ? "pointer" : undefined }}
						onClick={(event) => {
							onClickFunc(event)
						}}>
						{OrderStateToHumanText[state]} {canEdit ? <PenIcon size={32} className={style.editIcon} /> : null}
						{statusElementDropdown(state)}
					</div>
				)
			},
			Cancelled: () => {
				return (
					<div
						className={cls(style.orderState, style.orderStateDeclined)}
						style={{ cursor: canEdit ? "pointer" : undefined }}
						onClick={(event) => {
							onClickFunc(event)
						}}>
						{OrderStateToHumanText[state]} {canEdit ? <PenIcon size={32} className={style.editIcon} /> : null}
						{statusElementDropdown(state)}
					</div>
				)
			},
			Done: () => {
				return (
					<div
						className={cls(style.orderState, style.orderStateDone)}
						style={{ cursor: canEdit ? "pointer" : undefined }}
						onClick={(event) => {
							onClickFunc(event)
						}}>
						{OrderStateToHumanText[state]} {canEdit ? <PenIcon size={32} className={style.editIcon} /> : null}
						{statusElementDropdown(state)}
					</div>
				)
			},
			_: () => null,
		})
	}

	function statusElementDropdown(state: OrderState): JSX.Element | null {
		const canEdit = canEditState(state)

		if (!showUpdateOrderStateDropdown || !canEdit) {
			return null
		}

		return (
			<div
				className={style.orderStateDropdown}
				style={{ top: showUpdateOrderStateDropdown.positionY, left: showUpdateOrderStateDropdown.positionX }}>
				{state === OrderState.Accepted ? null : (
					<div
						key={OrderState.Accepted + "_selectNewState"}
						onClick={() => {
							updateOrderState(OrderState.Accepted)
						}}>
						{OrderStateToHumanText[OrderState.Accepted]}
					</div>
				)}
				{state === OrderState.InProgress ? null : (
					<div
						key={OrderState.InProgress + "_selectNewState"}
						onClick={() => {
							updateOrderState(OrderState.InProgress)
						}}>
						{OrderStateToHumanText[OrderState.InProgress]}
					</div>
				)}
				{state === OrderState.Declined ? null : (
					<div
						key={OrderState.Declined + "_selectNewState"}
						onClick={() => {
							updateOrderState(OrderState.Declined)
						}}>
						{OrderStateToHumanText[OrderState.Declined]}
					</div>
				)}
				{state === OrderState.Cancelled ? null : (
					<div
						key={OrderState.Cancelled + "_selectNewState"}
						onClick={() => {
							updateOrderState(OrderState.Cancelled)
						}}>
						{OrderStateToHumanText[OrderState.Cancelled]}
					</div>
				)}
				{state === OrderState.Done ? null : (
					<div
						key={OrderState.Done + "_selectNewState"}
						onClick={() => {
							updateOrderState(OrderState.Done)
						}}>
						{OrderStateToHumanText[OrderState.Done]}
					</div>
				)}
			</div>
		)
	}

	function updateOrderState(state: OrderState) {
		const orderToUse = getCurrentOrderDetails()

		if (updatingOrderState || !orderToUse) {
			return
		}

		if (orderToUse.state === OrderState.Done) {
			confirmModalRef.current?.open(
				`Denna order har status: "${
					OrderStateToHumanText[orderToUse.state]
				}", är du säker att du vill ändra status till "${OrderStateToHumanText[state]}"?`,
				null,
				(reason) => {
					confirmModalRef.current?.close()
					if (reason === ConfirmModalCloseReason.Accept) {
						callUpdateOrderState(orderToUse.id, state)
					} else {
						setShowUpdateOrderStateDropdown(null)
					}
				},
			)
			return
		}

		if (state === OrderState.Cancelled) {
			confirmModalRef.current?.open(
				`Vill du ändra status på ordern till "${OrderStateToHumanText[state]}"?`,
				null,
				(reason) => {
					confirmModalRef.current?.close()
					if (reason === ConfirmModalCloseReason.Accept) {
						callUpdateOrderState(orderToUse.id, state)
					} else {
						setShowUpdateOrderStateDropdown(null)
					}
				},
			)
			return
		}

		callUpdateOrderState(orderToUse.id, state)
	}

	function callUpdateOrderState(orderId: string, state: OrderState) {
		setUpdatingOrderState(true)

		const updateOrder: UpdateOrderStateDirectly = {
			newState: state,
		}

		API.postWithRetries<GetOrder>(
			`/customer-portal/orders/client-v1/${client.identifier}/set-state-directly/${orderId}`,
			updateOrder,
		).then(
			() => {
				setShowUpdateOrderStateDropdown(null)
				setUpdatingOrderState(false)
				fetchOrder()
			},
			() => {
				setUpdatingOrderState(false)
			},
		)
	}

	function createdBy() {
		const orderToUse = getCurrentOrderDetails()

		if (!orderToUse) {
			return null
		}

		let creator
		if (orderToUse.loggedInUser && orderToUse.loggedInUser.email !== orderToUse.customer.email) {
			creator = `${orderToUse.loggedInUser.firstName || ""} ${orderToUse.loggedInUser.lastName || ""}`
		}

		if (!creator && orderToUse.customer.ordererName) {
			creator = orderToUse.customer.ordererName
		}
		return (
			<div className={"twoLineClamp"} style={{ wordBreak: "break-word", hyphens: "auto" }}>
				Skapad{" "}
				{creator ? (
					<span>
						av <strong>{creator}</strong>
						{", "}
					</span>
				) : null}
				{DateTime.fromSeconds(orderToUse.createdAt)
					.setLocale("sv-SE")
					.toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY)}
			</div>
		)
	}

	function topInfoBar() {
		if (under711Width && selectedMobileOnlyTab !== MobileOnlyTabs.Order) {
			return null
		}

		const orderToUse = getCurrentOrderDetails()

		if (!orderToUse) {
			return null
		}

		let klarnaReference
		let discountCode
		let klarnaPaymentFailed = false

		klarnaReference = orderToUse.payment?.type === "Klarna" ? orderToUse.payment.orderReference : null
		discountCode = orderToUse.discountCode

		if (!klarnaReference && orderToUse.consumerGroupSettings?.type === "Prepay") {
			klarnaPaymentFailed = true
		}

		let customExternalReference: ExternalReference | null = null

		if (client.features.orderUiOrderReferencesMode === OrderReferenceMode.ShowExternalReference) {
			customExternalReference = (orderToUse.references.find((x) => x.type === "ExternalReference") ||
				null) as ExternalReference | null
		}

		const coredinationJobRef = order?.references.find((x) => x.type === "CoredinationJobReference") as
			| CoredinationJobReference
			| undefined

		let orderNumberText = `Order #${orderToUse.orderNumber}`

		return (
			<div className={cls(style.topInfoBar, "google-maps-custom-hidden-fullscreen")}>
				<div className={style.topInfoBarSpacer}></div>
				<div className={style.orderNumberAndCreationDate}>
					<div className={style.orderNumber}>{orderNumberText} </div>
					{!under711Width ? createdBy() : null}
				</div>

				{under711Width ? (
					<div className={style.orderStatus} id={"orderStatusElementWrapper"}>
						{statusElement(orderToUse.state)}
					</div>
				) : null}

				{!under711Width ? (
					<HorizontalScrollBox
						key={"topInfoBarScroll"}
						className={style.horizontalScrollWrapper}
						cellClassName={style.horizontalScrollCell}>
						<div className={style.orderStatus} id={"orderStatusElementWrapper"}>
							{statusElement(orderToUse.state)}
						</div>

						{klarnaReference ? (
							<div className={style.reference}>
								Klarna referensnummer: <strong>{klarnaReference}</strong>
							</div>
						) : null}

						{klarnaPaymentFailed ? (
							<div className={style.reference}>
								Klarna referensnummer: <span className={style.failureTextPill}>Gick ej igenom</span>
							</div>
						) : null}

						{customExternalReference ? (
							<div className={style.reference}>
								Fri referens: <strong>{customExternalReference.value}</strong>
							</div>
						) : null}

						{auth.IsLoggedInClient && coredinationJobRef && !!coredinationJobRef.jobNo ? (
							<div className={style.reference}>
								Coredination-uppdrag: <strong>{coredinationJobRef.jobNo}</strong>
							</div>
						) : null}

						{discountCode ? (
							<div className={style.reference}>
								Rabattkod: <strong>{discountCode.identifier}</strong>
							</div>
						) : null}
					</HorizontalScrollBox>
				) : null}
			</div>
		)
	}

	function mobileOnlyTabs() {
		if (!under711Width) {
			return null
		}

		return (
			<div
				className={cls(style.mobileOnlyTabsWrapper, {
					[style.mobileOnlyTabsWrapperHidden]:
						!showMobileOnlyTabs && selectedMobileOnlyTab === MobileOnlyTabs.Order,
				})}>
				<HorizontalScrollBox
					className={style.horizontalScrollWrapper}
					cellClassName={style.horizontalScrollCell}
					key={`mobileOnlyTabsScroll`}>
					<div
						className={cls(style.mobileOnlyTab, {
							[style.selectedTab]: selectedMobileOnlyTab === MobileOnlyTabs.Order,
						})}
						onClick={() => {
							setSelectedMobileOnlyTab(MobileOnlyTabs.Order)
						}}>
						<EmptyClipboard size={18} className={style.mobileOnlyTabIcon} />
						<span>Bokning</span>
					</div>
					{client.features.orderActivityRenderMode !== OrderActivityRenderMode.NoRender &&
					permissions.isAllowed(PermissionAreaLocation.Order_Activities_Read) ? (
						<div
							className={cls(style.mobileOnlyTab, {
								[style.selectedTab]: selectedMobileOnlyTab === MobileOnlyTabs.Activities,
							})}
							onClick={() => {
								setSelectedMobileOnlyTab(MobileOnlyTabs.Activities)
								fetchActivities()
							}}>
							<WaveSignal size={18} className={style.mobileOnlyTabIcon} />
							<span>Aktivitet</span>
							<span
								className={cls(style.attachmentsTabAmountCircle, {
									[style.attachmentsTabAmountCircleZero]: (activities || []).length === 0,
								})}>
								{(activities || []).length}
							</span>
						</div>
					) : null}
					{permissions.isAllowed(PermissionAreaLocation.Order_Attachments_Read) ? (
						<div
							className={cls(style.mobileOnlyTab, {
								[style.selectedTab]: selectedMobileOnlyTab === MobileOnlyTabs.Attachments,
							})}
							onClick={() => {
								setSelectedMobileOnlyTab(MobileOnlyTabs.Attachments)
							}}>
							<Paperclip1 size={18} className={style.mobileOnlyTabIcon} />
							<span>Bilagor</span>
							<span
								className={cls(style.attachmentsTabAmountCircle, style.accentColor, {
									[style.attachmentsTabAmountCircleZero]: attachments.length === 0,
								})}>
								{attachments.length}
							</span>
						</div>
					) : null}
				</HorizontalScrollBox>
			</div>
		)
	}

	function customerInfoAndOrdererRow() {
		const orderToUse = getCurrentOrderDetails()

		if (!orderToUse) {
			return null
		}

		let customerName = orderToUse.customer.name
		let customerIdentifier
		let type: "Company" | "Person"

		if ("orgNumber" in orderToUse.customer) {
			type = "Company"
			customerIdentifier = orderToUse.customer.orgNumber
		} else {
			type = "Person"
			customerIdentifier = orderToUse.customer.personNumber
		}

		let ordererName = orderToUse.customer.ordererName
		let phone = orderToUse.customer.phone
		let email = orderToUse.customer.email

		return (
			<div
				className={cls(
					{ [style.fiftyFiveFortyFiveColumns]: !!ordererName || !!phone || !!email },
					{ [style.oneColumn]: !ordererName && !phone && !email },
				)}>
				<div className={style.regularBox}>
					<div className={style.boxTitleAndIcon}>
						<div className={style.boxIcon}>
							<UserSingleNeutralMale size={18} />
						</div>
						<div className={style.boxTitle}>Kund</div>
					</div>
					<div
						className={style.boxInsideBox}
						style={{ display: "flex", gap: "5px", justifyContent: "space-between" }}>
						<div className={style.customerNameAndIdentifierWrapper}>
							<span className={style.customerName}>{customerName}</span>
							<span className={style.customerIdentifier}>{customerIdentifier}</span>
						</div>
						<div className={style.customerInfoTagsDropdownWrapper}>
							<div
								className={style.customerInfoTagsDropdownButton}
								onClick={() => setShowConsumerTags(!showConsumerTags)}>
								Taggar
								<FontAwesomeIcon
									className={cls({ [style.customerInfoTagsDropdownOpen]: showConsumerTags })}
									icon={faAngleDown}
								/>
							</div>
							{showConsumerTags ? (
								<div className={style.customerInfoTagsDropdown}>
									<div className={style.textPill} style={{ whiteSpace: "nowrap" }}>
										{type === "Company" ? "Företagskund" : "Privatpersonskund"}
									</div>
									{customerInfoRelatedTags()}
								</div>
							) : null}
						</div>
					</div>
				</div>
				{!!ordererName || !!phone || !!email ? (
					<div className={style.regularBox}>
						<div className={style.boxTitleAndIcon}>
							<div className={style.boxIcon}>
								<IncomingCall size={18} />
							</div>
							<div className={style.boxTitle}>Beställare</div>
						</div>
						<div className={style.boxInsideBox}>
							<div style={{ display: "flex", gap: "5px", justifyContent: "space-between" }}>
								<div className={style.ordererNameAndPhone}>
									{ordererName ? <strong>{ordererName}</strong> : null}
									{phone ? <strong>{formatPhoneNumberForSweden(phone)}</strong> : null}
									<span style={{ wordBreak: "break-all" }}>{email}</span>
								</div>
								{!!phone || !!email ? (
									<div className={style.ordererMailAndCallButtonsWrapper}>
										{!!email ? (
											<AccentButton
												className={style.mailToButton}
												onClick={() => {
													window.open(`mailto:${email}`, "_self")
												}}>
												<MailSendEnvelope size={18} />
											</AccentButton>
										) : null}

										{!!phone ? (
											<AccentButton
												className={style.callToButton}
												onClick={() => {
													window.open(
														"tel:" + formatPhoneNumberForSwedenToInternational(phone),
														"_self",
													)
												}}>
												<OutgoingCall size={18} />
											</AccentButton>
										) : null}
									</div>
								) : null}
							</div>
						</div>
					</div>
				) : null}
			</div>
		)
	}

	function customerInfoRelatedTags() {
		if (!auth.IsLoggedInClient) {
			return null
		}

		return (
			<div style={{ display: "flex", gap: "5px", flexDirection: "column" }}>
				{orderConsumerTags.map((tag) => {
					return (
						<div key={tag.id + tag.text} className={style.textPill} style={{ whiteSpace: "nowrap" }}>
							{tag.text}
						</div>
					)
				})}
			</div>
		)
	}

	function orderTitleAndDescriptionRow() {
		if (!auth.IsLoggedInClient) {
			return null
		}

		const orderToUse = getCurrentOrderDetails()

		if (!orderToUse || (!orderToUse.description && !orderToUse.title)) {
			return null
		}

		return (
			<div
				className={cls(
					{ [style.fiftyFiftyColumns]: !!orderToUse.title && !!orderToUse.description },
					{
						[style.oneColumn]:
							(!orderToUse.title && !!orderToUse.description) ||
							(!!orderToUse.title && !orderToUse.description),
					},
				)}>
				{orderToUse.title ? (
					<div className={style.regularBox}>
						<div className={style.boxTitleAndIcon}>
							<div className={style.boxIcon}>
								<ChatBubbleTextSquare size={18} />
							</div>
							<div className={style.boxTitle}>Titel</div>
						</div>
						<div className={style.boxInsideBox}>{orderToUse.title}</div>
					</div>
				) : null}
				{orderToUse.description ? (
					<div className={style.regularBox}>
						<div className={style.boxTitleAndIcon}>
							<div className={style.boxIcon}>
								<ChatBubbleSquareWrite size={18} />
							</div>
							<div className={style.boxTitle}>Beskrivning</div>
						</div>
						<div className={style.boxInsideBox}>{orderToUse.description}</div>
					</div>
				) : null}
			</div>
		)
	}

	function adressAndContactPersonsRow() {
		const orderToUse = getCurrentOrderDetails()

		if (!orderToUse) {
			return null
		}

		const adress = orderToUse.address

		return (
			<div className={style.oneColumn}>
				<div className={style.regularBox}>
					<div className={style.boxTitleAndIcon}>
						<div className={style.boxIcon}>
							<LocationPin3 size={18} />
						</div>
						<div className={style.boxTitle}>{orderToUse.marking ? "Projekt" : "Adress"}</div>
					</div>
					<div className={style.multipleBoxesInsideBox}>
						<div className={style.box}>
							<div className={style.boxTitleAndIcon}>
								<div className={style.boxIcon}>
									<MapPin size={18} />
								</div>
								<div className={style.boxTitle}>Leveransadress</div>
							</div>
							<div className={style.boxContent} style={{ flexWrap: "wrap" }}>
								<span>
									<strong>{adress.street}, </strong>
									{adress.zipcode ? adress.zipcode + " " : ""}
									{adress.city}
								</span>
								<AccentButton
									className={style.showMapButton}
									onClick={() => {
										setShowMapSection(!showMapSection)
									}}>
									{showMapSection ? "Dölj" : "Karta"}
								</AccentButton>
							</div>
						</div>
						{orderToUse.marking ? (
							<div className={style.box}>
								<div className={style.boxTitleAndIcon}>
									<div className={style.boxIcon}>
										<TagIcon size={18} />
									</div>
									<div className={style.boxTitle}>Märkning / Littera</div>
								</div>
								<div className={style.boxContent}>{orderToUse.marking}</div>
							</div>
						) : null}
						{!!orderToUse.contact || (!!orderToUse.contactPersons && orderToUse.contactPersons.length > 0) ? (
							<div className={style.box}>
								<div className={style.boxTitleAndIcon}>
									<div className={style.boxIcon}>
										<UserMultipleGroup size={18} />
									</div>
									<div className={style.boxTitle}>Kontaktperson(er)</div>
								</div>
								{addressContactPersonSection()}
							</div>
						) : null}
					</div>

					{mapRow()}
				</div>
			</div>
		)
	}

	function addressContactPersonSection() {
		const orderToUse = getCurrentOrderDetails()

		if (!orderToUse) {
			return null
		}

		if (orderToUse.contact) {
			return (
				<div className={style.boxContent} style={{ justifyContent: "space-between" }}>
					<div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
						<strong className={"twoLineClamp"} title={orderToUse.contact.name}>
							{orderToUse.contact.name}
						</strong>
						{orderToUse?.contact?.phone ? (
							<span>{formatPhoneNumberForSweden(orderToUse?.contact?.phone)}</span>
						) : null}
					</div>
					{!!orderToUse?.contact?.phone ? (
						<AccentButton
							className={style.callToButton}
							onClick={() => {
								window.open(
									"tel:" + formatPhoneNumberForSwedenToInternational(orderToUse?.contact?.phone),
									"_self",
								)
							}}>
							Ring
						</AccentButton>
					) : null}
				</div>
			)
		} else {
			let contactPersons: Contact[] = orderToUse?.contactPersons || []

			if (contactPersons.length > 1) {
				return contactPersonsDropdown(contactPersons)
			}

			if (!!contactPersons[0]) {
				return contactPersonRowElement(contactPersons[0])
			}

			return null
		}
	}

	function contactPersonRowElement(person: Contact) {
		return (
			<div
				key={person.name + getKey(person)}
				className={style.boxContent}
				style={{ justifyContent: "space-between" }}>
				<div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
					<strong className={"twoLineClamp"} title={person.name}>
						{person.name}
					</strong>
					<span>{formatPhoneNumberForSweden(person.phone)}</span>
					{person.email ? <span className="twoLineClamp">{person.email}</span> : null}
				</div>
				{!!person.phone || !!person.email ? (
					<div className={style.contactPersonMailAndCallButtonsWrapper}>
						{!!person.email ? (
							<AccentButton
								className={style.mailToButton}
								onClick={() => {
									window.open(`mailto:${person.email}`, "_self")
								}}>
								<MailSendEnvelope size={18} />
							</AccentButton>
						) : null}

						{!!person.phone ? (
							<AccentButton
								className={style.callToButton}
								onClick={() => {
									window.open("tel:" + formatPhoneNumberForSwedenToInternational(person.phone), "_self")
								}}>
								<OutgoingCall size={18} />
							</AccentButton>
						) : null}
					</div>
				) : null}
			</div>
		)
	}

	function contactPersonsDropdown(contactPersons: Contact[]) {
		return (
			<div className={style.contactPersonsDropdownWrapper}>
				<AccentButton
					className={style.showMoreContactPersonsButton}
					onClick={() => {
						setShowAllContactPersons(!showAllContactPersons)
					}}>
					{showAllContactPersons ? "Dölj kontaktpersoner" : "Visa kontaktpersoner"}
					{showAllContactPersons ? <FontAwesomeIcon icon={faAngleUp} /> : <FontAwesomeIcon icon={faAngleDown} />}
				</AccentButton>

				{showAllContactPersons ? (
					<div className={style.contactPersonsDropdown}>
						{contactPersons.map((person) => {
							return contactPersonRowElement(person)
						})}
					</div>
				) : null}
			</div>
		)
	}

	function timeAndDateRow() {
		const orderToUse = getCurrentOrderDetails()

		if (!orderToUse) {
			return null
		}

		return (
			<div className={style.fiftyFiftyColumns}>
				<div className={style.regularBox}>
					<div className={style.boxTitleAndIcon}>
						<div className={style.boxIcon}>
							<CircleClock size={18} />
						</div>
						<div className={style.boxTitle}>Tid</div>
					</div>
					<div className={cls(style.boxInsideBox, style.flexAlignItemsCenter)}>
						{orderToUse.datetime?.timeExact || orderToUse.datetime?.timeDescription || ""}
					</div>
				</div>
				<div className={style.regularBox}>
					<div className={style.boxTitleAndIcon}>
						<div className={style.boxIcon}>
							<BlankCalendar size={18} />
						</div>
						<div className={style.boxTitle}>Datum</div>
					</div>
					<div className={cls(style.boxInsideBox, style.flexAlignItemsCenter)}>
						{completedOrderItemDateElementContent(orderToUse.datetime)}
					</div>
				</div>
			</div>
		)
	}

	function tabsSection() {
		const orderToUse = getCurrentOrderDetails()

		if (!orderToUse) {
			return null
		}

		const orderArticles = orderToUse.orderArticles

		const hasArticles =
			some(orderToUse?.products || [], (product) => {
				if (isNumber(product)) {
					return false
				}

				return product.productArticles.length > 0
			}) || orderArticles.length > 0

		const coredinationJobRef = orderToUse?.references.find((x) => x.type === "CoredinationJobReference") as
			| CoredinationJobReference
			| undefined

		const coredinationOrderRef = orderToUse?.references.find((x) => x.type === "CoredinationOrderReference") as
			| CoredinationOrderReference
			| undefined

		let articlesAndProductsName

		if (
			(coredinationJobRef && coredinationJobRef.source === ExternalReferenceSource.Coredination) ||
			(coredinationOrderRef && coredinationOrderRef.source === ExternalReferenceSource.Coredination)
		) {
			articlesAndProductsName = "Artiklar"
		} else {
			if (hasArticles) {
				articlesAndProductsName = "Artiklar & produkter"
			} else {
				articlesAndProductsName = "Produkter"
			}
		}

		const tabs: InlineViewTab[] = [
			!under1101Width
				? {
						id: "articlesAndProducts",
						name: articlesAndProductsName,
						content: articlesAndProductsSection(),
						disabled: false,
						iconElement: <BriefcaseDollar size={18} />,
				  }
				: null,
			{
				id: "orderNotes",
				name: "Anteckningar",
				content: notesSection(),
				disabled: false,
				iconElement: <EmptyClipboard size={18} />,
			},
			!under1101Width && permissions.isAllowed(PermissionAreaLocation.Order_Attachments_Read)
				? {
						id: "attachments",
						name: `Bilagor`,
						afterNameElement: (
							<span
								className={cls(style.attachmentsTabAmountCircle, {
									[style.attachmentsTabAmountCircleZero]: attachments.length === 0,
								})}>
								{attachments.length}
							</span>
						),
						content: attachmentsSection(),
						disabled: false,
						iconElement: <Paperclip1 size={18} />,
				  }
				: null,
			!under1101Width
				? {
						id: "transportation",
						name: `Transport`,
						content: transportSection(),
						disabled: false,
						iconElement: <ShippingTruck size={18} />,
				  }
				: null,
			{
				id: "externalDetails",
				name: "Externa detaljer",
				content: externalDetailsSection(),
				disabled: false,
				iconElement: <CloudShare size={18} />,
			},
		].filter(notNull)

		return <TabbedInlineView wrapperStyles={{ marginTop: "30px" }} tabs={tabs} />
	}

	function mapRow() {
		const orderToUse = getCurrentOrderDetails()
		if (!orderToUse || !isGoogleMapsLoaded || !showMapSection) {
			return null
		}

		let project = orderToUse.address
		let exactDeliveryCoordinates = orderToUse.execution?.deliveryCoordinates || null

		return (
			<div className={style.mapAndAddressContainer}>
				{project?.coordinates ? (
					<GoogleMapComponent
						mapContainerClassName={style.mapContainer}
						center={project?.coordinates || stockholmCenterCoordinates}
						zoom={17}
						onLoad={(map) => {
							mapRef.current = map
							initGoogleMapsCustomFullscreenControl(map)

							currentMapMarkers.current = [...addAddressAndExactDeliveryMarkersToMap(map, orderToUse)]
							maybeSetMapBoundsForExactDelivery(map, orderToUse)

							addTransportZoneOverlaysToMap(
								map,
								mainMapOverlaysRef,
								mainMapOverlaysMarkersRef,
								client.transportZonesSorted,
								client,
								project,
								null,
							)
						}}
						options={googleMiniMapOptions(true, "cooperative")}
					/>
				) : null}
				{project?.coordinates || exactDeliveryCoordinates ? (
					<div className={style.googleMapsButtons} style={{ marginTop: "10px" }}>
						{project?.coordinates ? (
							<div
								className={style.googleMapButton}
								onClick={() => {
									openCoordinatesInGoogleMaps(project?.coordinates)
								}}>
								Öppna adress i Google Maps
							</div>
						) : null}
						{exactDeliveryCoordinates ? (
							<div
								className={style.googleMapButton}
								onClick={() => {
									openCoordinatesInGoogleMaps(exactDeliveryCoordinates)
								}}>
								Öppna leveransplats i Google Maps
							</div>
						) : null}
					</div>
				) : null}
			</div>
		)
	}

	function transportSection() {
		const orderToUse = getCurrentOrderDetails()

		if (!orderToUse) {
			return null
		}

		const transportation: Transportation | null = orderToUse?.products?.[0]?.transportation || null
		const transportZoneName = orderToUse.transportZoneName
		const address = orderToUse.address

		if (!transportation && !transportZoneName) {
			return null
		}

		return (
			<div className={style.transportSectionWrapper}>
				{!!transportZoneName || (!!address && address.street && address.city) ? (
					<div className={style.addressTransportZoneField}>
						{transportZoneName ? (
							<span className={style.addressTransportZoneName}>
								<span>Transportzon:</span> <strong>{transportZoneName}</strong>
							</span>
						) : null}
						{!transportZoneName && !!address ? <strong>Ingen transportzon identifierad</strong> : null}
					</div>
				) : null}
				{!!transportation ? (
					<div className={style.transportationInfo}>
						<div className={style.boxTitleAndIcon}>
							<div className={style.boxIcon}>
								<ShippingTruck size={18} />
							</div>
							<div className={style.boxTitle}>
								<span>Valt leveranssätt</span>
								{transportation?.name ? (
									<span className={style.chosenTransportationMethodName}>{transportation?.name}</span>
								) : null}
							</div>
						</div>
						<TransportDescription
							id={"transportMethodTooltipSVG" + transportation.name}
							type={transportation.type}
							data={transportation.data}
						/>
					</div>
				) : null}
			</div>
		)
	}

	function externalDetailsSection() {
		const orderToUse = getCurrentOrderDetails()

		if (!orderToUse) {
			return null
		}

		let klarnaReference = orderToUse.payment?.type === "Klarna" ? orderToUse.payment.orderReference : null

		let klarnaPaymentFailed = false
		if (!klarnaReference && orderToUse.consumerGroupSettings?.type === "Prepay") {
			klarnaPaymentFailed = true
		}

		return (
			<div className={style.externalDetailsSectionWrapper}>
				{externalIntegrationsSection()}
				{customOrderReferenceSection()}

				{klarnaReference ? (
					<div className={style.regularBox}>
						<div className={style.boxTitleAndIcon}>
							<div className={style.boxTitle}>Klarna</div>
						</div>
						<div className={cls(style.boxInsideBox, style.flexAlignItemsCenter)} style={{ gap: "10px" }}>
							Referensnummer: <strong>{klarnaReference}</strong>
						</div>
					</div>
				) : null}

				{klarnaPaymentFailed ? (
					<div className={style.regularBox}>
						<div className={style.boxTitleAndIcon}>
							<div className={style.boxTitle}>Klarna</div>
						</div>
						<div className={cls(style.boxInsideBox, style.flexAlignItemsCenter)} style={{ gap: "5px" }}>
							Referensnummer: <span className={style.failureTextPill}>Gick ej igenom</span>
						</div>
					</div>
				) : null}
			</div>
		)
	}

	function saveCustomOrderReference() {
		if (!auth.IsLoggedInClient) {
			return
		}

		const orderToUse = getCurrentOrderDetails()
		const input = customOrderReferenceInputRef.current

		if (!orderToUse || !input) {
			return
		}

		setUpdating(true)
		const reference = orderToUse.references.find((x) => x.type === "ExternalReference")
		const req: UpdateCustomOrderReference = {
			reference: {
				type: "ExternalReference",
				id: reference?.id || ObjectID().toHexString(),
				value: input.value || "",
			},
		}
		API.postWithRetries<SetOrderReferenceResponse, UpdateCustomOrderReference>(
			`/customer-portal/orders/client-v1/${client.identifier}/custom-order-reference/${orderToUse.id}`,
			req,
			{},
			15,
		).then(
			async () => {
				await fetchOrder()
				setUpdating(false)
				setEditCustomOrderReference(false)
			},
			() => {
				setUpdating(false)
			},
		)
	}

	function customOrderReferenceSection(): JSX.Element | null {
		if (client.features.orderUiOrderReferencesMode !== OrderReferenceMode.ShowExternalReference) {
			return null
		}

		const orderToUse = getCurrentOrderDetails()
		if (!orderToUse) {
			return null
		}

		let buttons: JSX.Element | null = null

		if (auth.IsLoggedInClient) {
			if (!editCustomOrderReference) {
				buttons = (
					<div style={{ display: "flex", alignItems: "center" }}>
						<PenIcon
							size={32}
							className={style.editIcon}
							onClick={() => {
								setEditCustomOrderReference(true)
							}}
						/>
					</div>
				)
			} else {
				if (updating) {
					buttons = (
						<div style={{ display: "flex", alignItems: "center" }}>
							<div className={style.loadingIcon}>
								<FontAwesomeIcon icon={faSpinner} spin={true} />
							</div>
						</div>
					)
				} else {
					buttons = (
						<div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
							<CrossIcon
								size={32}
								className={style.cancelIcon}
								onClick={() => {
									setEditCustomOrderReference(false)
								}}
							/>
							<div
								className={style.okIcon}
								onClick={() => {
									saveCustomOrderReference()
								}}>
								<FontAwesomeIcon icon={faCheck} />
							</div>
						</div>
					)
				}
			}
		}

		let section

		if (
			permissions.isAllowed(PermissionAreaLocation.Client_Order_External_Reference_Edit) &&
			editCustomOrderReference
		) {
			let defaultValue = (
				orderToUse.references.find((x) => x.type === "ExternalReference") as ExternalReference | undefined
			)?.value

			section = (
				<div className={style.editCustomOrderReferenceInputWrapper}>
					<label>
						<input
							ref={customOrderReferenceInputRef}
							readOnly={updating}
							disabled={updating}
							defaultValue={defaultValue}
							className={style.input}
							type="text"
							placeholder="Egen orderreferens"
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									saveCustomOrderReference()
								}
							}}
						/>
					</label>
					{buttons}
				</div>
			)
		} else {
			let reference =
				(orderToUse.references.find((x) => x.type === "ExternalReference") as ExternalReference | undefined)
					?.value || ""

			if (reference) {
				section = (
					<div className={cls(style.blockContent, style.spacedRow)}>
						<div style={{ fontSize: "16px", display: "flex", alignItems: "center", height: "32px" }}>
							<strong>{reference}</strong>
						</div>
						{auth.IsLoggedInClient &&
						permissions.isAllowed(PermissionAreaLocation.Client_Order_External_Reference_Edit) ? (
							<div style={{ display: "flex", alignItems: "center" }}>
								<PenIcon
									size={32}
									className={style.editIcon}
									onClick={() => {
										setEditCustomOrderReference(true)
									}}
								/>
							</div>
						) : null}
					</div>
				)
			} else {
				if (auth.IsLoggedInConsumer) {
					return null
				}

				section = (
					<div className={cls(style.blockContent, style.spacedRow, style.addFreeReferenceFieldWrapper)}>
						<div className={style.addFreeReferenceField}>Lägg till referens</div>
						<div
							className={style.pillButton}
							onClick={() => {
								setEditCustomOrderReference(true)
							}}>
							Välj
						</div>
					</div>
				)
			}
		}

		return (
			<div className={style.regularBox}>
				<div className={style.boxTitleAndIcon} style={{ marginBottom: "16px" }}>
					<div className={style.boxTitle}>Fri referens</div>
				</div>
				{section}
			</div>
		)
	}

	function externalIntegrationsSection() {
		if (!auth.IsLoggedInClient) {
			return null
		}

		const orderToUse = getCurrentOrderDetails()

		if (!orderToUse) {
			return null
		}

		const coredinationOrderRef = orderToUse?.references.find((x) => x.type === "CoredinationOrderReference") as
			| CoredinationOrderReference
			| undefined
		const coredinationJobRef = orderToUse?.references.find((x) => x.type === "CoredinationJobReference") as
			| CoredinationJobReference
			| undefined

		if (!coredinationOrderRef && !coredinationJobRef) {
			return null
		}

		return [
			coredinationOrderRef ? (
				<div key={"externalIntegration_coredination_order"} className={style.regularBox}>
					<div className={style.boxTitleAndIcon}>
						<div className={style.boxTitle}>Coredination-beställning</div>
					</div>
					<div className={cls(style.boxInsideBox, style.flexAlignItemsCenter)}>
						{coredinationOrderRef.orderNo != null ? (
							<span>Beställningsnummer: {coredinationOrderRef.orderNo}</span>
						) : null}
						<div
							style={{ marginLeft: "auto" }}
							className={style.pillButton}
							onClick={() => {
								if (!coredinationOrderRef || coredinationOrderRef.type !== "CoredinationOrderReference") {
									return
								}

								window.open(
									`https://web.app.coredination.net/#orders/${coredinationOrderRef.uuid}`,
									"_blank",
								)
							}}>
							Öppna beställning
						</div>
					</div>
				</div>
			) : null,
			coredinationJobRef ? (
				<div key={"externalIntegration_coredination_job"} className={style.regularBox}>
					<div className={style.boxTitleAndIcon}>
						<div className={style.boxTitle}>Coredination-uppdrag</div>
					</div>
					<div className={cls(style.boxInsideBox, style.flexAlignItemsCenter)}>
						{coredinationJobRef.jobNo != null ? <span>Uppdragsnummer: {coredinationJobRef.jobNo}</span> : null}
						<div
							style={{ marginLeft: "auto" }}
							className={style.pillButton}
							onClick={() => {
								if (!coredinationJobRef || coredinationJobRef.type !== "CoredinationJobReference") {
									return
								}

								window.open(`https://web.app.coredination.net/#jobs/${coredinationJobRef.uuid}`, "_blank")
							}}>
							Öppna uppdrag
						</div>
					</div>
				</div>
			) : null,
		].filter(notNull)
	}

	function notesSection() {
		const orderToUse = getCurrentOrderDetails()

		if (!orderToUse) {
			return null
		}

		let customerName = orderToUse.customer.name
		let consumerCommentFromRealOrder = orderToUse.comment
		let clientCommentFromRealOrder = orderToUse.clientComment

		let loggedInUser: GetOrderLoggedInUser | null = null

		if (orderToUse.loggedInUser) {
			const loggedInName = `${orderToUse.loggedInUser.firstName || ""} ${orderToUse.loggedInUser.lastName || ""}`
				.normalize("NFC")
				.toLocaleLowerCase()

			const ordererName = orderToUse.customer?.ordererName?.normalize("NFC")?.toLocaleLowerCase() || ""

			if (loggedInName !== ordererName && orderToUse.loggedInUser.email !== orderToUse.customer.email) {
				loggedInUser = orderToUse.loggedInUser
			}
		}

		return (
			<div className={style.extraInformationSectionWrapper}>
				{customerName ? (
					<div className={style.regularBox}>
						<div className={style.boxTitleAndIcon}>
							<div className={style.boxIcon}>
								<EmptyClipboard size={18} />
							</div>
							<div className={style.boxTitle}>Kommentar från orderläggare</div>
						</div>
						<div className={cls(style.boxInsideBox, style.flexAlignItemsCenter)}>
							{consumerCommentFromRealOrder}
						</div>
					</div>
				) : null}

				<div className={style.regularBox}>
					<div className={style.boxTitleAndIcon}>
						<div className={style.boxIcon}>
							<EmptyClipboard size={18} />
						</div>
						<div className={style.boxTitle}>Kommentar från {client.clientInfo.clientName}</div>
					</div>
					<div className={cls(style.boxInsideBox, style.flexAlignItemsCenter)}>{clientCommentFromRealOrder}</div>
				</div>

				{loggedInUser ? (
					<div className={style.regularBox}>
						<div className={style.boxTitleAndIcon}>
							<div className={style.boxIcon}>
								<UserCheckValidate size={18} />
							</div>
							<div className={style.boxTitle}>Orderläggare</div>
						</div>
						<div className={style.boxInsideBox}>
							<div className={style.loggedInUserWrapper}>
								<div className={style.profilePic}>
									{loggedInUser.profilePictureUrl ? (
										<img
											src={loggedInUser.profilePictureUrl}
											onError={(e) => {
												;(e.target as HTMLImageElement).src = ""
											}}
											alt=""
										/>
									) : (
										<div className={style.noProfilePic}>
											{convertTextToInitials(loggedInUser.firstName + " " + loggedInUser.lastName)}
										</div>
									)}
								</div>
								<div>
									<div>
										{loggedInUser.userType === OrderLoggedInUserType.Client
											? client.clientInfo.clientName
											: orderToUse.customer.name}
									</div>
									<div>
										<strong>
											{loggedInUser.firstName} {loggedInUser.lastName}
										</strong>
										, {loggedInUser.email}
									</div>
								</div>
							</div>
						</div>
					</div>
				) : null}
			</div>
		)
	}

	function attachmentsSection() {
		const orderToUse = getCurrentOrderDetails()
		if (
			!permissions.isAllowed(PermissionAreaLocation.Order_Attachments_Read) ||
			!orderToUse ||
			(!client.features.orderUiAllowUploadingOrderImages && attachments.length === 0)
		) {
			return null
		}

		return (
			<div className={style.attachmentsSectionWrapper}>
				<OrderDetailsAttachments
					titleElement={
						<div className={style.boxTitleAndIcon}>
							<div className={style.boxIcon}>
								<Paperclip1 size={18} />
							</div>
							<div className={style.boxTitle}>Bilagor</div>
							{under711Width ? (
								<CrossIcon
									size={21}
									className={style.goBackIcon}
									onClick={() => {
										setSelectedMobileOnlyTab(MobileOnlyTabs.Order)
									}}
								/>
							) : null}
						</div>
					}
					hideEmptySections={true}
					customer={orderToUse.customer}
					orderId={orderToUse.id}
					files={attachments}
					onAmountChanged={() => {
						fetchAttachments()
					}}
				/>
			</div>
		)
	}

	function articlesAndProductsSection() {
		const orderToUse = getCurrentOrderDetails()
		if (!orderToUse) {
			return null
		}

		let discountCode = orderToUse.discountCode || null

		return (
			<div className={style.articlesAndProductsSectionWrapper}>
				{productsSection()}
				{articleSection()}
				{discountCode ? (
					<div className={style.regularBox}>
						<div className={style.boxTitleAndIcon}>
							<div className={style.boxIcon}>
								<ReceiptSubtract size={18} />
							</div>
							<div className={style.boxTitle}>Rabattkod</div>
						</div>
						<div className={cls(style.boxInsideBox, style.flexAlignItemsCenter)} style={{ gap: "4px" }}>
							<strong>{discountCode.identifier}</strong>
							<span>{discountCode.label}</span>
						</div>
					</div>
				) : null}
			</div>
		)
	}

	function productsSection() {
		const orderToUse = getCurrentOrderDetails()

		if (!orderToUse) {
			return null
		}

		const coredinationJobRef = orderToUse?.references.find((x) => x.type === "CoredinationJobReference") as
			| CoredinationJobReference
			| undefined

		if (coredinationJobRef && coredinationJobRef.source === ExternalReferenceSource.Coredination) {
			return null
		}

		return (
			<div className={style.regularBox}>
				<div className={style.boxTitleAndIcon}>
					<div className={style.boxIcon}>
						<ShoppingBasket2 size={20} />
					</div>
					<div className={style.boxTitle}>Produkter</div>
				</div>
				<HorizontalScrollBox
					key={"productsAndServicesHorizontalScroll"}
					className={style.horizontalScrollWrapper}
					cellClassName={cls(style.horizontalScrollCell, style.orderProductInfoBoxScrollCell)}>
					{orderToUse?.products.map(orderProductToInfoBox)}
				</HorizontalScrollBox>
			</div>
		)
	}

	function orderProductToInfoBox(orderProduct: OrderProduct) {
		const orderToUse = getCurrentOrderDetails()

		if (!orderToUse) {
			return null
		}

		let productImages = orderProduct.productImages
		let categoryName = orderProduct.category?.name
		let serviceName = orderProduct.service?.name || orderProduct?.packagingMethod?.name
		let wasteTypeName = orderProduct.wasteType?.name

		const key = getKey(orderProduct)
		return (
			<div key={key} className={style.orderProductBox}>
				<div className={style.orderProductBoxImage}>
					{productImages ? (
						"url" in productImages.main ? (
							<img
								style={{
									maxWidth: "100%",
									height: "100%",
									maxHeight: "65px",
									margin: "0 auto",
									objectFit: "contain",
								}}
								src={productImages.main.url}
								alt="Produktbild"
							/>
						) : (
							<ProductImage
								client={client}
								categoryName={categoryName}
								image={ProductImageType[productImages.main.typeImage]}
							/>
						)
					) : (
						<ProductImage
							client={client}
							categoryName={categoryName}
							image={
								"typeImage" in orderProduct && orderProduct.typeImage
									? ProductImageType[orderProduct.typeImage]
									: undefined
							}
						/>
					)}
				</div>
				<div className={style.orderProductBoxName}>
					<div className={style.firstRow}>{unitFormatter(orderProduct.name)}</div>
					{serviceName ? <div className={style.secondRow}>{serviceName}</div> : null}
					{wasteTypeName ? <div className={style.thirdRow}>{wasteTypeName}</div> : null}
				</div>
				<div className={style.orderProductBoxQuantity}>
					{orderProduct.amount} {productServiceUnitToHumanText(orderProduct.unit)}
				</div>
			</div>
		)
	}

	function articleSection() {
		const orderToUse = getCurrentOrderDetails()

		if (!orderToUse) {
			return null
		}

		let orderArticles = orderToUse.orderArticles

		const hasArticles =
			some(orderToUse?.products || [], (product) => {
				if (isNumber(product)) {
					return false
				}

				return product.productArticles.length > 0
			}) || orderArticles.length > 0

		if (!hasArticles) {
			return null
		}

		return (
			<div className={style.regularBox}>
				<div className={style.boxTitleAndIcon}>
					<div className={style.boxIcon}>
						<ReceiptSubtract size={20} />
					</div>
					<div className={style.boxTitle}>Artiklar</div>
				</div>

				<ToggleSwitchSlimWithText
					className={style.toggleSwitchVAT}
					styles={{ marginTop: "20px", marginBottom: "20px" }}
					text={"Visa priser med moms"}
					onChange={(event) => {
						setPreferredVATRenderPolicy(() => {
							if (event.target.checked) {
								return PreferredVATRenderPolicy.IncludeVAT
							}
							return PreferredVATRenderPolicy.ExcludeVAT
						})
					}}
					checked={preferredVATRenderPolicy !== PreferredVATRenderPolicy.ExcludeVAT}
				/>

				<div className={style.articlesSection}>
					<div className={style.leftSide}>
						<div className={style.articleRows}>
							{(orderToUse?.products || []).flatMap((product) => {
								let productArticles = product.productArticles

								return productArticles.map((productArticle, index) => {
									const path = productArticle.articlePath
										.map((y) => y.pathKey + (y.targetKey || ""))
										.join("")
									const key = path ? path : v4()

									let theNumber: number = when(preferredVATRenderPolicy, {
										[PreferredVATRenderPolicy.ExcludeVAT]: () => {
											return productArticle.price
										},
										[PreferredVATRenderPolicy.IncludeVAT]: () => {
											return productArticle.price * (1 + productArticle.taxPercentage)
										},
										[PreferredVATRenderPolicy.IncludeVATExplicit]: () => {
											return productArticle.price * (1 + productArticle.taxPercentage)
										},
									})

									return (
										<div key={"asd1_" + key + index} className={style.articleRow}>
											<div className={style.articleName}>
												{product.name}{" "}
												{product?.wasteType?.name ? ` - ${product?.wasteType?.name}` : null}
											</div>
											{theNumber > 0 ? (
												<div className={style.articleQuantity}>
													{productArticle.amount} {productServiceUnitToHumanText(product.unit)} x{" "}
													{currencyFormatter(theNumber)}
												</div>
											) : (
												<div></div>
											)}
											<div className={style.articlePrice}>
												{currencyFormatter(theNumber * productArticle.amount)}
											</div>
										</div>
									)
								})
							})}
							{orderBy(orderArticles, (x) => x.type).flatMap((orderArticle) => {
								let theNumber: number = when(preferredVATRenderPolicy, {
									[PreferredVATRenderPolicy.ExcludeVAT]: () => {
										return orderArticle.price
									},
									[PreferredVATRenderPolicy.IncludeVAT]: () => {
										return orderArticle.price * (1 + orderArticle.taxPercentage)
									},
									[PreferredVATRenderPolicy.IncludeVATExplicit]: () => {
										return orderArticle.price * (1 + orderArticle.taxPercentage)
									},
								})

								if (orderArticle.type === ArticleType.Discount) {
								} else {
								}

								const name = exhaustive(orderArticle, "type", {
									[ArticleType.Transport]: () => {
										const treeName = orderArticle.articlePath.find((x) => x.targetKey === "Tree")?.name

										// default value
										let info = <span>Transportavgift</span>

										let zoneIdIndex = orderArticle.articlePath.findIndex(
											(x) => x.targetKey === BranchTargetKeyEnum.Zone,
										)
										let transportationIdIndex = orderArticle.articlePath.findIndex(
											(x) => x.targetKey === BranchTargetKeyEnum.Transportation,
										)

										// try to show the name of the transport method or transport zone, depending on what type we find last
										let zoneName, transportationName
										if (zoneIdIndex > -1 && transportationIdIndex === -1) {
											// only zone found
											zoneName = orderArticle.articlePath[zoneIdIndex]?.name
										} else if (zoneIdIndex === -1 && transportationIdIndex > -1) {
											// only transportation found
											transportationName = orderArticle.articlePath[transportationIdIndex]?.name
										} else if (zoneIdIndex > -1 && transportationIdIndex > -1) {
											// both found
											if (zoneIdIndex > transportationIdIndex) {
												// zone is later than transport, use it
												zoneName = orderArticle.articlePath[zoneIdIndex]?.name
											} else if (transportationIdIndex > zoneIdIndex) {
												// transportation is later than zone, use it
												transportationName = orderArticle.articlePath[transportationIdIndex]?.name
											}
										} else {
											// none found
										}

										if (zoneName) {
											info = (
												<span>
													{treeName} - <strong>{zoneName}</strong>
												</span>
											)
										} else if (transportationName) {
											info = (
												<span>
													{treeName} - <strong>{transportationName}</strong>
												</span>
											)
										}

										return info
									},
									[ArticleType.Product]: () => {
										// order article of type product should never happen, so this will always return null
										return null
									},
									[ArticleType.DateSlot]: () => {
										const name =
											orderToUse.datetime.dateSlot?.name ||
											orderArticle.articlePath.find(
												(x) => x.targetKey === BranchTargetKeyEnum.DateSlot,
											)?.name ||
											null

										return (
											<span>
												Datumavgift - <strong>{name}</strong>
											</span>
										)
									},
									[ArticleType.Discount]: () => {
										const tree =
											orderArticle.articlePath.find((x) => x.targetKey === BranchTargetKeyEnum.Tree)
												?.name || null
										const discTemplate =
											orderArticle.articlePath.find(
												(x) => x.targetKey === BranchTargetKeyEnum.DiscountTemplate,
											)?.name || null

										if (!tree || !discTemplate) {
											return null
										}

										return (
											<span>
												{tree} - <strong>{discTemplate}</strong>
											</span>
										)
									},
									_: () => {
										return orderArticle.articlePath.map((x) => x.name).join(" - ")
									},
								})

								return (
									<div key={getKey(orderArticle)} className={style.articleRow}>
										<div className={style.articleName}>{name}</div>
										{theNumber > 0 ? (
											<div className={style.articleQuantity}>
												{orderArticle.amount} st x {currencyFormatter(theNumber)}
											</div>
										) : (
											<div></div>
										)}
										<div className={style.articlePrice}>
											{currencyFormatter(theNumber * orderArticle.amount)}
										</div>
									</div>
								)
							})}
						</div>
					</div>
					<div className={style.rightSide}>
						<div className={style.articleSummaryBox}>
							<div className={style.articleSummaryAmounts}>
								<div>
									<strong style={{ fontSize: "18px" }}>Varor -</strong>{" "}
									<span>
										{sum([
											...(orderToUse?.products || []).map((x) => x.amount || 0),
											...orderArticles
												.filter((x) => x.type !== ArticleType.Transport)
												.map((x) => x.amount),
										])}{" "}
										st
									</span>
								</div>
								<div style={{ fontWeight: 600 }}>
									{currencyFormatter(
										sum([
											...(orderToUse?.products || []).flatMap((x) => {
												return x.productArticles.map((y) => {
													let taxPercentage: number = 0
													when(preferredVATRenderPolicy, {
														[PreferredVATRenderPolicy.ExcludeVAT]: () => {},
														[PreferredVATRenderPolicy.IncludeVAT]: () => {
															taxPercentage = y.taxPercentage
														},
														[PreferredVATRenderPolicy.IncludeVATExplicit]: () => {
															taxPercentage = y.taxPercentage
														},
													})

													return y.price * (1 + taxPercentage) * y.amount
												})
											}),
											...orderArticles
												.filter((x) => x.type !== ArticleType.Transport)
												.map((x) => {
													let taxPercentage: number = 0
													when(preferredVATRenderPolicy, {
														[PreferredVATRenderPolicy.ExcludeVAT]: () => {},
														[PreferredVATRenderPolicy.IncludeVAT]: () => {
															taxPercentage = x.taxPercentage
														},
														[PreferredVATRenderPolicy.IncludeVATExplicit]: () => {
															taxPercentage = x.taxPercentage
														},
													})

													return x.price * (1 + taxPercentage) * x.amount
												}),
										]),
									)}
								</div>
							</div>
							<div className={style.articleSummaryAmounts}>
								<div>
									<strong style={{ fontSize: "18px" }}>Transportavgift -</strong>{" "}
									<span>
										{sum(
											orderArticles
												.filter((x) => x.type === ArticleType.Transport)
												.map((x) => x.amount),
										)}{" "}
										st
									</span>
								</div>
								<div style={{ fontWeight: 600 }}>
									{currencyFormatter(
										sum(
											orderArticles
												.filter((x) => x.type === ArticleType.Transport)
												.map((x) => {
													let taxPercentage: number = 0
													when(preferredVATRenderPolicy, {
														[PreferredVATRenderPolicy.ExcludeVAT]: () => {},
														[PreferredVATRenderPolicy.IncludeVAT]: () => {
															taxPercentage = x.taxPercentage
														},
														[PreferredVATRenderPolicy.IncludeVATExplicit]: () => {
															taxPercentage = x.taxPercentage
														},
													})

													return x.price * (1 + taxPercentage) * x.amount
												}),
										),
									)}
								</div>
							</div>
							<div className={style.articleSummaryTotals}>
								<div className={style.totalRow}>
									<strong style={{ fontSize: "24px", fontWeight: 600 }}>Totalt</strong>
									<div style={{ fontSize: "24px", fontWeight: 600 }}>
										{currencyFormatter(
											sum([
												...(orderToUse?.products || []).flatMap((x) => {
													return x.productArticles.map((y) => {
														let taxPercentage: number = 0
														when(preferredVATRenderPolicy, {
															[PreferredVATRenderPolicy.ExcludeVAT]: () => {},
															[PreferredVATRenderPolicy.IncludeVAT]: () => {
																taxPercentage = y.taxPercentage
															},
															[PreferredVATRenderPolicy.IncludeVATExplicit]: () => {
																taxPercentage = y.taxPercentage
															},
														})

														return y.price * (1 + taxPercentage) * y.amount
													})
												}),
												...orderArticles.map((x) => {
													let taxPercentage: number = 0
													when(preferredVATRenderPolicy, {
														[PreferredVATRenderPolicy.ExcludeVAT]: () => {},
														[PreferredVATRenderPolicy.IncludeVAT]: () => {
															taxPercentage = x.taxPercentage
														},
														[PreferredVATRenderPolicy.IncludeVATExplicit]: () => {
															taxPercentage = x.taxPercentage
														},
													})

													return x.price * (1 + taxPercentage) * x.amount
												}),
											]),
										)}
									</div>
								</div>
								{preferredVATRenderPolicy !== PreferredVATRenderPolicy.ExcludeVAT ? (
									<div className={style.totalRow}>
										<div>Varav moms</div>
										<div>
											{currencyFormatter(
												sum([
													...(orderToUse?.products || []).flatMap((x) => {
														return x.productArticles.map(
															(y) => y.price * y.taxPercentage * y.amount,
														)
													}),
													...orderArticles.map((x) => x.price * x.taxPercentage * x.amount),
												]),
											)}
										</div>
									</div>
								) : null}
							</div>
						</div>
					</div>
				</div>
			</div>
		)
	}

	function activityBoxInModal() {
		if (
			!order ||
			client.features.orderActivityRenderMode === OrderActivityRenderMode.NoRender ||
			!permissions.isAllowed(PermissionAreaLocation.Order_Activities_Read) ||
			!showActivitiesInModal
		) {
			return null
		}

		return (
			<ModulePopup
				key={"activityBoxInModal"}
				className={style.activityBoxInModal}
				onClose={() => {
					setShowActivitiesInModal(false)
				}}>
				<div className={style.activitiesModalContentWrapper}>{activityBox()}</div>
			</ModulePopup>
		)
	}

	function activityBox() {
		if (
			!order ||
			client.features.orderActivityRenderMode === OrderActivityRenderMode.NoRender ||
			!permissions.isAllowed(PermissionAreaLocation.Order_Activities_Read)
		) {
			return null
		}

		if (!activities) {
			return (
				<div style={{ display: "flex", justifyContent: "center" }}>
					<Loader />
				</div>
			)
		}

		return (
			<div className={style.activityWrapper}>
				<div className={style.activitySectionTop}>
					<div className={style.nameAndIcon}>
						<div className={style.icon}>
							<WaveSignal size={26} />
						</div>
						<div className={style.name}>Aktivitet</div>
						{under711Width ? (
							<CrossIcon
								size={21}
								className={style.goBackIcon}
								onClick={() => {
									setSelectedMobileOnlyTab(MobileOnlyTabs.Order)
								}}
							/>
						) : null}
					</div>

					{auth.IsLoggedInClient &&
					permissions.isAllowed(PermissionAreaLocation.Client_Order_Activities_Internal_Read) ? (
						<MultiStateSwitch
							enableSmallerSizeOnSmallerScreens={true}
							wrapperClass={style.activitiesMultiStateSwitch}
							selectedValue={showActivitiesType}
							onSelect={(option, initial) => {
								if (initial || option.key === showActivitiesType) {
									return
								}

								setShowActivitiesType(option.key)
							}}
							options={[
								{ key: ShowActivitiesType.External, value: "Extern vy" },
								{ key: ShowActivitiesType.Internal, value: "Intern vy" },
							]}
						/>
					) : null}

					{auth.IsLoggedInClient && showActivitiesType === ShowActivitiesType.External ? (
						<div style={{ display: "flex" }}>
							<div className={style.activitySectionUnderTabsInfoText}>Kunden är med i denna chatt!</div>
						</div>
					) : null}
				</div>
				<div className={style.activitiesBox}>
					<OrderDetailsActivity
						disableUploadFiles={
							client.externalIntegrationSettings.coredination.mode !== CoredinationMode.Disabled
						}
						customer={order.customer}
						orderId={order.id}
						activities={activities.filter((activity) => {
							return exhaustive(activity, "type", {
								Message: (it) => {
									if (showActivitiesType === ShowActivitiesType.Internal) {
										return it.internal === true
									} else {
										return it.internal === false
									}
								},
								StateChange: () => {
									return true
								},
							})
						})}
						files={attachments}
						internalActivities={
							showActivitiesType === ShowActivitiesType.Internal &&
							auth.IsLoggedInClient &&
							permissions.isAllowed(PermissionAreaLocation.Client_Order_Activities_Internal_Read)
						}
						onMessageSend={async (message) => {
							await fetchAttachments()
							setActivities((activities) => {
								if (activities) {
									activities.push(message)
									return sortBy(activities, "createdAt").reverse()
								} else {
									return [message]
								}
							})
						}}
					/>
				</div>
			</div>
		)
	}

	function acceptOrDenyOnSubmit() {
		if (!selectedOutcome || !order || order.hasBeenAcceptedOrDeniedAtOnePoint) {
			return
		}

		setUpdating(true)

		const updateOrder: UpdateOrderStateAndComment = {
			state: selectedOutcome === "approve" ? OrderState.Accepted : OrderState.Declined,
			comment: acceptOrDenyComment,
		}
		API.postWithRetries<GetOrder>(
			`/customer-portal/orders/client-v1/${client.identifier}/set-state-and-comment/${order.id}`,
			updateOrder,
		).then(
			async () => {
				await fetchOrder()
				setUpdating(false)
			},
			() => {
				setUpdating(false)
			},
		)
	}

	function acceptOrDenySection() {
		if (
			!order ||
			!auth.IsLoggedInClient ||
			!permissions.isAllowed(PermissionAreaLocation.Client_Orders_Update) ||
			order.state !== OrderState.Created ||
			order.hasBeenAcceptedOrDeniedAtOnePoint
		) {
			return null
		}

		const coredinationJobRef = order?.references.find((x) => x.type === "CoredinationJobReference") as
			| CoredinationJobReference
			| undefined

		const coredinationOrderRef = order?.references.find((x) => x.type === "CoredinationOrderReference") as
			| CoredinationOrderReference
			| undefined

		if (
			(coredinationJobRef && coredinationJobRef.source === ExternalReferenceSource.Coredination) ||
			(coredinationOrderRef && coredinationOrderRef.source === ExternalReferenceSource.Coredination)
		) {
			return null
		}

		return (
			<div className={style.acceptOrDenySection}>
				<div className={style.buttonsWrapper}>
					<AcceptButton
						onClick={() => {
							if (selectedOutcome === "approve") {
								setSelectedOutcome(null)
							} else {
								setSelectedOutcome("approve")
							}
						}}
						className={cls({ [style.selectedOutcome]: selectedOutcome === "approve" }, style.outcome)}>
						<FontAwesomeIcon className={style.iconColorAccept} icon={faCheck} />
						Acceptera
					</AcceptButton>
					<DenyButton
						onClick={() => {
							if (selectedOutcome === "deny") {
								setSelectedOutcome(null)
							} else {
								setSelectedOutcome("deny")
							}
						}}
						className={cls({ [style.selectedOutcome]: selectedOutcome === "deny" }, style.outcome)}>
						<CrossIcon size={22} className={style.iconColorDeny} iconClassName={style.closeXIconInside} />
						Neka
					</DenyButton>
				</div>
				{selectedOutcome !== null ? (
					<textarea
						id="commentInput"
						name="commentInput"
						className={style.commentInput}
						value={acceptOrDenyComment}
						placeholder="Lägg till en kommentar som skickas till kunden"
						onChange={(e) => {
							setAcceptOrDenyComment(e.target.value)
						}}
					/>
				) : null}
				{selectedOutcome !== null ? (
					<FinalizeButton disabled={updating} onClick={acceptOrDenyOnSubmit}>
						Skicka bekräftelse&nbsp;
						{updating ? <FontAwesomeIcon style={{ color: "gray" }} spin={true} icon={faSpinner} /> : null}
					</FinalizeButton>
				) : null}
			</div>
		)
	}

	function mainContent() {
		const hideSideSection =
			client.features.orderActivityRenderMode === OrderActivityRenderMode.NoRender ||
			!permissions.isAllowed(PermissionAreaLocation.Order_Activities_Read)

		let showFloatingButton = false
		if (
			client.features.orderActivityRenderMode !== OrderActivityRenderMode.NoRender &&
			permissions.isAllowed(PermissionAreaLocation.Order_Activities_Read) &&
			showFloatingActivitiesButton
		) {
			showFloatingButton = true
		}

		return (
			<div
				className={cls(
					style.wrapper,
					{
						[style.activitiesTabShown]: under711Width && selectedMobileOnlyTab === MobileOnlyTabs.Activities,
					},
					{
						[style.attachmentsTabShown]: under711Width && selectedMobileOnlyTab === MobileOnlyTabs.Attachments,
					},
				)}>
				{activityBoxInModal()}
				{showFloatingButton ? (
					<div
						key={"topInfoBarActivitiesButton"}
						className={style.floatingActivitiesButton}
						onClick={() => {
							setShowActivitiesInModal(true)
						}}>
						<div className={style.content}>
							<WaveSignal className={style.notificationIcon} size={30} />
							<div className={style.text}>{activities?.length || 0}</div>
						</div>
					</div>
				) : null}
				{mobileOnlyTabs()}

				{under711Width && selectedMobileOnlyTab === MobileOnlyTabs.Order ? (
					<div
						style={{ marginTop: "70px" }}
						className={style.goBackButton}
						onClick={() => {
							navigator.open(`my-pages/orders`)
						}}>
						<LeftArrowIcon size={32} className={style.goBackIcon} />
						<div className={style.text}>
							<strong>Visa alla ordrar</strong>
						</div>
					</div>
				) : null}

				{topInfoBar()}
				<div className={style.contentSection}>
					{mainSection(hideSideSection)}
					{!hideSideSection && !under1900Width ? <div className={style.sideSection}>{activityBox()}</div> : null}
				</div>
			</div>
		)
	}

	function mainSection(hideSideSection: boolean) {
		if (under711Width && selectedMobileOnlyTab !== MobileOnlyTabs.Order) {
			switch (selectedMobileOnlyTab) {
				case MobileOnlyTabs.Activities:
					return activityBox()
				case MobileOnlyTabs.Attachments:
					return attachmentsSection()
			}
		}

		return (
			<div
				className={style.mainSection}
				style={{
					flexBasis: hideSideSection || under1900Width ? "100%" : undefined,
				}}>
				{!under711Width ? (
					<div
						className={style.goBackButton}
						onClick={() => {
							navigator.open(`my-pages/orders`)
						}}>
						<LeftArrowIcon size={32} className={style.goBackIcon} />
						<div className={style.text}>
							<strong>Visa alla ordrar</strong>
						</div>
					</div>
				) : null}
				{acceptOrDenySection()}
				{customerInfoAndOrdererRow()}
				{orderTitleAndDescriptionRow()}
				{adressAndContactPersonsRow()}
				{timeAndDateRow()}
				{under1101Width ? articlesAndProductsSection() : null}
				{under1101Width ? transportSection() : null}
				{under1101Width && !under711Width ? attachmentsSection() : null}
				{tabsSection()}

				{under711Width ? <div style={{ marginTop: "50px" }}>{createdBy()}</div> : null}
			</div>
		)
	}

	if (!order) {
		return (
			<div className={style.wrapper}>
				<AbsolutCentered>
					<Loader />
				</AbsolutCentered>
			</div>
		)
	}

	return (
		<>
			<ConfirmModal
				ref={confirmModalRef}
				text={""}
				customContent={null}
				closedByDefault={true}
				className={style.confirmModal}
				onClose={() => {}}
			/>
			{mainContent()}
		</>
	)
}
